/**
 * Lumio assistant chat transport: the real backend (replaces the former mock).
 *
 * Wired the same way as the rest of the owner-hub data layer (axios + the shared
 * `Authorization` token), just pointed DIRECTLY at the Lumio service on its own host
 * (`lumioApiUrl`, NOT the owner-hub MLP proxy) and with the chat two-step:
 *
 *   1. POST {lumioApiUrl}/chat/init  · { language, timezone } -> opens a conversation
 *      and returns a `conversation_id` (plus greeting, suggested_topics and
 *      tenant_context). Called once per conversation, lazily on the first turn.
 *   2. POST {lumioApiUrl}/chat/       · { message, conversation_id, language } ->
 *      { response, conversation_id, ... }. The full reply arrives in ONE shot
 *      (not a token stream), so we keep the typewriter feel by emitting the reply
 *      word-by-word via `onToken`, abortable through the same `AbortSignal`.
 *
 * Auth: `Authorization: Bearer <access_token>`, the same token the rest of the app
 * uses, read straight from localStorage so this stays free of the redux Provider
 * (the chat panel renders outside one in tests). NOTE the `Bearer ` prefix: the
 * owner-hub APIs take the RAW token, but the Lumio service expects `Bearer`. Every
 * Lumio call opts OUT of the global auth interceptor's refresh-and-teardown
 * (`_owsNoAuthRecovery`): Lumio is a SEPARATE service on its own host, so a 401/403
 * from it must not rotate the owner-hub token or log the user out. It fails silently
 * into the chat's own retry affordance instead. See `src/utils/auth-interceptor.ts`.
 *
 * Security: the reply is REMOTE, untrusted markdown. It is rendered through
 * `markdownToHtml` (see `markdown.ts`), which escapes every raw run before
 * composing tags and gates link hrefs, so no markup in a reply can inject HTML.
 *
 * Full request/response contract: `docs/api.owners-hub.lumio-chat.md`.
 */
import axios, { type AxiosRequestConfig } from 'axios';

import {
  buildLumioPageContext,
  initPageContext,
  type LumioInitContext,
  type LumioPageContext,
} from '../../../../lib/lumioPageContext';
import type { LumioSignals } from '../../../../lib/lumioSignals';
import { LUMIO_CHAT } from '../../../../theme/tokens';

const INIT_PATH = '/chat/init';
const CHAT_PATH = '/chat/';
const HISTORY_PATH = '/chat/history';
const CONVERSATIONS_PATH = '/chat/conversations';

/* ── transport contract (kept source-compatible with the former mock) ──────── */

export interface LumioRequest {
  prompt: string;
  /**
   * Current page section, e.g. "Acquisition". Used for the panel's section tag;
   * the richer per-turn page context (section included) is captured at send
   * time via `buildLumioPageContext` and sent in the request's `context` field.
   */
  section: string;
  signal: AbortSignal;
  /** Called with each reply chunk as it is "typed out". */
  onToken: (chunk: string) => void;
  /** Reuse an established conversation; `null` -> open a new one (init) first. */
  conversationId: string | null;
  /** UI language code (i18n), forwarded to the assistant. */
  language: string;
}

/** One producer-declared decision chip on a pending proposal. Producers can
 * declare several — multiple `accept` variants ("Approve €100" / "Halve it —
 * €50") and/or `deny` variants — each with its own `id`, which travels back to
 * the producer so the exact variant executes. `decision` is the terminal state
 * the chip maps onto (the machine knows only accept/deny); `label` is free text.
 * `note` is an optional one-line clarification, surfaced as the chip's tooltip. */
export interface ProposalAction {
  id: string;
  label: string;
  decision: 'accept' | 'deny';
  note?: string;
}

/** A proposal awaiting the owner's decision, with its action chips. */
export interface PendingProposal {
  proposal_id: string;
  title: string;
  /** The producer's plain-language rationale (what was flagged, the current
   * state, what each choice does) — shown under the title so the owner decides
   * on the reasoning, not just the headline. May be absent on older payloads. */
  summary?: string;
  actions: ProposalAction[];
}

export interface LumioResult {
  /** Full content streamed so far (partial if aborted). */
  content: string;
  /** True if the caller aborted before completion. */
  aborted: boolean;
  /** True if the request failed. */
  error?: boolean;
  /** The conversation to reuse next turn (freshly minted when we just init'd). */
  conversationId: string | null;
  /** Owner-inbox proposals with decision chips as of this reply; `null` when
   * the field was absent (player mode / not carried on this turn). Present
   * only once the reply is received (undefined on abort-before-reply). */
  pendingProposals?: PendingProposal[] | null;
}

/* ── API response shapes (probed live; see the doc) ────────────────────────── */

export interface LumioCasino {
  id: string;
  tenant_id: string;
  brand_name: string;
  domain: string;
  logo: string | null;
  status: string;
  operating_region: string | null;
}

/** `POST /chat/init` */
export interface LumioInitResponse {
  session_id: string;
  conversation_id: string;
  greeting: string;
  persona_name: string;
  language: string;
  user_context: {
    user_id: string;
    is_guest: boolean;
    display_name: string | null;
    user_role: string;
    vip_status: string | null;
    active_promotions: unknown;
    preferences: unknown;
  };
  tenant_context: {
    owner_user_id: string;
    owner_email: string;
    casinos_total: number;
    casinos: LumioCasino[];
  };
  suggested_topics: string[];
  /** Owner inbox as of session open — proposals with decision chips, so the
   * panel can render them before the first message. Absent/null when none. */
  pending_proposals?: PendingProposal[] | null;
  prefetch_status: string;
  timestamp: string;
}

/** `POST /chat/` */
export interface LumioSendResponse {
  response: string;
  conversation_id: string;
  agent_used: string | null;
  interaction_count: number;
  session_metadata: Record<string, unknown> | null;
  response_time_ms: number | null;
  access_error: string | null;
  error_count: number;
  suggestions: string[] | null;
  pending_proposals: PendingProposal[] | null;
  sources: unknown;
  confidence_score: number | null;
  debug_context: unknown;
}

/* ── helpers ───────────────────────────────────────────────────────────────── */

const rand = (min: number, max: number): number => min + Math.floor(Math.random() * (max - min + 1));

/** Abortable sleep: resolves (never rejects) on either timeout or abort. */
const wait = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const onAbort = (): void => {
      window.clearTimeout(id);
      resolve();
    };
    const id = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });

/** `window.config` key holding the Lumio service base URL. */
const LUMIO_API_URL_KEY = 'lumioApiUrl';

/** The Lumio service base (`lumioApiUrl`) with any trailing slashes stripped; `''` when unset. */
const lumioBaseUrl = (): string => {
  const value = (window.config as unknown as Record<string, unknown> | undefined)?.[LUMIO_API_URL_KEY];
  return (typeof value === 'string' ? value : '').replace(/\/+$/, '');
};

/**
 * Lumio transport. Lumio is a SEPARATE service reached DIRECTLY on its own host
 * (`lumioApiUrl`), NOT through the owner-hub MLP proxy: it authenticates with the
 * shared owner-hub access token as `Bearer` (the owner-hub APIs take the RAW token,
 * but the Lumio service expects `Bearer`), and EVERY call opts out of the global auth
 * interceptor's refresh-and-teardown (`_owsNoAuthRecovery`), so a 401/403 from it must
 * NOT rotate the owner-hub token or tear the session down. The error rejects straight
 * to `streamLumioReply`, which surfaces it as a silent, retryable chat error. The
 * POSTs send a JSON body, hence `Content-Type`. See `src/utils/auth-interceptor.ts`.
 */
const lumioRequestConfig = (signal?: AbortSignal, params?: Record<string, unknown>): AxiosRequestConfig => {
  // The shared owner-hub access token, read from localStorage so this stays free of
  // the redux Provider (the chat panel renders outside one in tests) and always reads
  // the freshest token. `Bearer ` prefix: the Lumio service expects it (owner-hub does not).
  const token = localStorage.getItem('access_token') || '';
  return {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    // A 401/403 from the separate Lumio host is not an owner-hub-session signal:
    // never refresh or tear down, reject to the caller instead.
    _owsNoAuthRecovery: true,
    signal,
    ...(params ? { params } : {}),
  };
};

/** A stale/expired `conversation_id` -> the backend answers 404 `Conversation not
 * found`. Recoverable: re-init and retry the turn once on a fresh conversation. */
const isConversationNotFound = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  const detail = (error.response?.data as { detail?: string } | undefined)?.detail;
  // Require BOTH the 404 status AND the documented detail, so an unrelated 404
  // (wrong path, gateway) surfaces as a clean error instead of a doubled request.
  return error.response?.status === 404 && detail === 'Conversation not found';
};

/* ── API calls ─────────────────────────────────────────────────────────────── */

/** Open a Lumio conversation; returns the `conversation_id` to send turns with. */
export const lumioChatInit = async (
  language: string,
  signal?: AbortSignal,
  context?: LumioInitContext | null,
): Promise<LumioInitResponse> => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const { data } = await axios.post<LumioInitResponse>(
    `${lumioBaseUrl()}${INIT_PATH}`,
    { language, timezone, ...(context ? { context } : {}) },
    lumioRequestConfig(signal),
  );
  return data;
};

/** Send one turn. Omitting `conversation_id` lets the backend open one implicitly. */
export const lumioChatSend = async (
  message: string,
  conversationId: string | null,
  language: string,
  signal?: AbortSignal,
  context?: LumioPageContext | null,
): Promise<LumioSendResponse> => {
  const { data } = await axios.post<LumioSendResponse>(
    `${lumioBaseUrl()}${CHAT_PATH}`,
    {
      message,
      language,
      ...(conversationId ? { conversation_id: conversationId } : {}),
      ...(context ? { context } : {}),
    },
    lumioRequestConfig(signal),
  );
  return data;
};

/**
 * A proactive nudge. The backend decides it (stable `id` + structured `params`)
 * AND renders its localized copy — `message`, and when it carries an action
 * chip, `action_label` + `action_message`. The frontend displays those strings
 * as-is; it holds no nudge copy of its own (see lumio `proactive_copy.py`). The
 * `id`/`params`/`casino_instance_id` are still echoed back in the outcome report.
 */
export interface LumioNudge {
  id: string;
  type: string; // suggestion | celebration | continuation | anomaly | orientation
  params: Record<string, string | number>;
  action: boolean; // whether to render an action chip
  priority: number;
  /** Upcoming notices stop being relevant when the occurrence starts. */
  expires_at?: string;
  /** The casino this nudge was about — echoed back in the outcome report so
   * per-casino nag state keys to the same casino the emit did. */
  casino_instance_id?: string;
  /** Localized copy, rendered by the backend. `message` is the nudge sentence;
   * the action fields are present only when `action` is true. */
  message?: string;
  action_label?: string;
  action_message?: string;
}

/** An "On this page" chip: a localized caption and the chat turn it sends.
 * Backend-owned (lumio `proactive_copy.py`); the frontend renders it verbatim. */
export interface PageAction {
  id: string;
  label: string;
  message: string;
}

/**
 * The "On this page" chips for every studio section, localized (`GET
 * /assistant/page-actions`). Fetched once per locale and picked by route on the
 * client, so navigation needs no round-trip. `{}` when no Lumio host is set.
 */
export const lumioPageActions = async (
  locale: string,
  signal?: AbortSignal,
): Promise<Record<string, PageAction[]>> => {
  if (!lumioBaseUrl()) return {};
  const { data } = await axios.get<{ sections?: Record<string, PageAction[]> }>(
    `${lumioBaseUrl()}/assistant/page-actions`,
    lumioRequestConfig(signal, { locale }),
  );
  return data?.sections ?? {};
};

/**
 * Post a page event (owner navigated / dwelled) and get back an optional nudge.
 * Not a chat turn. `signals` are the canonical page facts the backend rules read;
 * `locale` only selects the language of the returned copy.
 * Returns null when the page isn't worth interrupting for, or no Lumio host is set.
 */
export const lumioContextEvent = async (
  context: { page: string; casino_instance_id: string },
  signals: LumioSignals,
  locale: string,
  signal?: AbortSignal,
): Promise<LumioNudge | null> => {
  if (!lumioBaseUrl()) return null;
  const { data } = await axios.post<{ nudge: LumioNudge | null }>(
    `${lumioBaseUrl()}/assistant/context-event`,
    { context, signals, locale },
    lumioRequestConfig(signal),
  );
  return data.nudge ?? null;
};

/** Report what the owner did with a nudge — the feedback loop (best-effort). */
export const lumioNudgeEvent = async (nudge: LumioNudge, outcome: 'clicked' | 'dismissed' | 'ignored'): Promise<void> => {
  if (!lumioBaseUrl()) return;
  try {
    await axios.post(
      `${lumioBaseUrl()}/assistant/nudge-event`,
      {
        nudge_id: nudge.id,
        outcome,
        casino_instance_id: nudge.casino_instance_id ?? '',
        params: nudge.params,
      },
      lumioRequestConfig(),
    );
  } catch {
    /* outcome reporting is best-effort; never surface an error */
  }
};

/**
 * The owner's pending proposals, fetched INDEPENDENTLY of chat (`GET
 * /proposals/pending`). This is the panel's real source for the inbox/badge: it
 * populates them on open and (polled) keeps them fresh as proposals are created
 * or decided — instead of relying on `/chat/init` (which the topics cache can
 * skip on a reload) and `/chat/` replies (only when the owner is chatting). The
 * `/chat/` reply's `pending_proposals` still applies as an immediate refinement.
 *
 * Returns `[]` when no Lumio host is configured; a network error rejects to the
 * caller, which treats failure as "keep what we already have" (never clears the
 * inbox on a blip). Same separate-Lumio-host transport as the chat calls.
 */
export const lumioPendingProposals = async (
  locale: string,
  signal?: AbortSignal,
): Promise<PendingProposal[]> => {
  if (!lumioBaseUrl()) return [];
  const { data } = await axios.get<{ proposals?: PendingProposal[] }>(
    `${lumioBaseUrl()}/proposals/pending`,
    lumioRequestConfig(signal, { locale }),
  );
  return Array.isArray(data?.proposals) ? data.proposals : [];
};

/** The outcome of a proposal decision, relayed for display in chat. */
export interface ProposalDecisionResult {
  proposal_id: string;
  status: string;
  action_id: string;
  message: string;
}

/**
 * Record the owner's click on a proposal action chip. Deterministic — no LLM
 * turn — so it can't be misclassified. Same separate-Lumio-host transport as
 * the chat calls (Bearer token, no owner-hub auth-recovery). The returned
 * `message` (including the producing service's reply) is shown in chat.
 */
export const lumioProposalDecision = async (
  proposalId: string,
  actionId: string,
  locale: string,
  signal?: AbortSignal,
): Promise<ProposalDecisionResult> => {
  const { data } = await axios.post<ProposalDecisionResult>(
    `${lumioBaseUrl()}/proposals/${encodeURIComponent(proposalId)}/decision`,
    { action_id: actionId },
    lumioRequestConfig(signal, { locale }),
  );
  return data;
};

/* ── conversation history (browse + resume) ───────────────────────────────────
 * /chat/conversations gives metadata only (no server title), newest-first and
 * paginated by page/page_size; we derive each list title from the first message
 * via /chat/history?...&limit=1. See docs/api.owners-hub.lumio-chat.md. */

/** A row from `GET /chat/conversations`. */
export interface LumioConversationSummary {
  conversation_id: string;
  /** ISO timestamp of the first message (NOT its text; may be zoneless UTC). */
  first_message: string | null;
  /** ISO timestamp of the last message (NOT its text; may be zoneless UTC). */
  last_message: string | null;
  /** Message count; 0 means an empty/unused conversation. */
  checkpoint_count: number;
}

/** A message from `GET /chat/history` (oldest-first). */
export interface LumioHistoryMessage {
  id: string;
  /** `human` is the owner's turn, `ai` is Lumio's. */
  type: 'human' | 'ai' | string;
  content: string;
  timestamp: string;
  agent: string | null;
  metadata: Record<string, unknown> | null;
}

/** List the owner's past conversations, newest-first. `limit` caps the page. */
export const lumioListConversations = async (
  limit: number,
  signal?: AbortSignal,
): Promise<LumioConversationSummary[]> => {
  const { data } = await axios.get<{ conversations?: LumioConversationSummary[] }>(
    `${lumioBaseUrl()}${CONVERSATIONS_PATH}`,
    lumioRequestConfig(signal, { page_size: limit }),
  );
  return Array.isArray(data?.conversations) ? data.conversations : [];
};

/** The first (oldest) message of a conversation: the source for its list title. */
export const lumioConversationFirstMessage = async (
  conversationId: string,
  signal?: AbortSignal,
): Promise<string | null> => {
  const { data } = await axios.get<{ messages?: LumioHistoryMessage[] }>(
    `${lumioBaseUrl()}${HISTORY_PATH}`,
    lumioRequestConfig(signal, { conversation_id: conversationId, limit: 1 }),
  );
  const first = Array.isArray(data?.messages) ? data.messages[0] : undefined;
  return first ? first.content : null;
};

/** The full message thread of a conversation (oldest-first), for resuming it. */
export const lumioConversationHistory = async (
  conversationId: string,
  signal?: AbortSignal,
): Promise<LumioHistoryMessage[]> => {
  const { data } = await axios.get<{ messages?: LumioHistoryMessage[] }>(
    `${lumioBaseUrl()}${HISTORY_PATH}`,
    lumioRequestConfig(signal, { conversation_id: conversationId }),
  );
  return Array.isArray(data?.messages) ? data.messages : [];
};

/* ── the streaming wrapper the chat hook consumes ──────────────────────────── */

/**
 * Resolve a reply for `prompt`, emitting it word-by-word via `onToken`. Ensures a
 * conversation exists (lazy init), recovers once from a stale conversation id, and
 * cooperates with the abort signal both during the network round-trip and the
 * type-out. NEVER rejects: failures come back as `{ error: true }` so the panel
 * can show its retry affordance; aborts come back as `{ aborted: true }`.
 */
export const streamLumioReply = async (req: LumioRequest): Promise<LumioResult> => {
  const { prompt, signal, onToken, language } = req;
  let conversationId = req.conversationId;

  // No Lumio service configured for this environment, surface the retryable
  // error state rather than firing a doomed request.
  if (!lumioBaseUrl()) {
    return { content: '', aborted: false, error: true, conversationId };
  }

  // Snapshot the visible page ONCE per turn, at send time, so the assistant
  // sees exactly what the user sees while typing. Never blocks the turn: a
  // capture failure just sends the message without context.
  let pageContext: LumioPageContext | null = null;
  try {
    pageContext = buildLumioPageContext();
  } catch {
    pageContext = null;
  }

  try {
    // 1) Ensure a conversation (lazy init on the first turn).
    if (!conversationId) {
      conversationId = (await lumioChatInit(language, signal, initPageContext(pageContext))).conversation_id;
    }
    if (signal.aborted) return { content: '', aborted: true, conversationId };

    // 2) Send the turn; recover once from a stale conversation by re-initialising.
    let reply: LumioSendResponse;
    try {
      reply = await lumioChatSend(prompt, conversationId, language, signal, pageContext);
    } catch (error) {
      if (!signal.aborted && isConversationNotFound(error)) {
        conversationId = (await lumioChatInit(language, signal, initPageContext(pageContext))).conversation_id;
        reply = await lumioChatSend(prompt, conversationId, language, signal, pageContext);
      } else {
        throw error;
      }
    }
    conversationId = reply.conversation_id || conversationId;
    // Inbox state as of this reply — carried on every return from here on, so
    // the chips stay in sync even if the owner stops the typing animation.
    const pendingProposals = reply.pending_proposals ?? null;
    if (signal.aborted) return { content: '', aborted: true, conversationId, pendingProposals };

    // 3) Type the reply out word-by-word (with trailing whitespace, so the
    //    reconstruction is exact) for the streaming feel.
    const tokens = (reply.response ?? '').match(/\S+\s*/g) ?? [reply.response ?? ''];
    let content = '';
    for (const token of tokens) {
      if (signal.aborted) return { content, aborted: true, conversationId, pendingProposals };
      content += token;
      onToken(token);
      await wait(rand(LUMIO_CHAT.tokenMinMs, LUMIO_CHAT.tokenMaxMs), signal);
    }
    // Reaching the end of the loop means every token was emitted, so the reply is
    // complete: do NOT report aborted even if Stop landed during the final wait.
    return { content, aborted: false, conversationId, pendingProposals };
  } catch {
    // A user-initiated stop surfaces as an axios cancel, treat it as aborted,
    // not as a failure (no error/retry affordance for an intentional stop).
    if (signal.aborted) return { content: '', aborted: true, conversationId };
    return { content: '', aborted: false, error: true, conversationId };
  }
};
